import type { Nullable } from "@core/domain/types/nullable.type";
import { normalizeText } from "@core/domain/utils/normalize-text";
import { BaseViewModel } from "@iot-platform/core";
import {
    SpatialLevel,
    SpatialLevels,
} from "@station/domain/models/spatial-level.model";
import type { StationListSearchFilters } from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { getInitialSuggestions } from "@station/presentation/utils/station-suggestions.utils";
import { Weather } from "@weather/domain/models/weather.model";
import { WeatherGetAllUseCase } from "@weather/domain/usecases/weather-get-all.usecase";
import type { SearchResult } from "@weather/presentation/pages/weather/weather.page";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class WeatherPageViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    weather: Nullable<Weather> = null;

    @observable
    stationId: Nullable<string> = null;

    @observable
    stationName: Nullable<string> = null;

    @observable
    stationTz = "";

    initialFiltersValue: StationListSearchFilters = {
        search: "",
    };

    @observable
    filters: StationListSearchFilters = this.initialFiltersValue;

    @observable
    searchResults: {
        id: number;
        label: string;
        isStation?: boolean;
        latitude?: Nullable<string>;
        longitude?: Nullable<string>;
        icon?: string;
    }[] = [];

    @observable
    initialLoading = true;

    @observable
    weatherLoading = false;

    constructor(
        @inject(WeatherGetAllUseCase)
        private readonly weatherGetAllUseCase: WeatherGetAllUseCase,
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.fetchWeather(), this.fetchSpatialLevels()]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    private async fetchSpatialLevels(): Promise<void> {
        this.spatialLevels = await this.spatialLevelGetAllUseCase.execute();
    }

    @action
    async fetchWeather(): Promise<void> {
        if (this.stationId) {
            this.weatherLoading = true;
            const weatherResult = await this.weatherGetAllUseCase.execute(
                this.stationId,
            );

            runInAction(() => {
                this.weather = weatherResult;
                this.stationTz = this.weather?.stationTz ?? "UTC";
                this.stationName = this.weather?.stationName ?? "-";
                this.weatherLoading = false;
            });
        }
    }

    @action
    setFilters(filters: { search: string }): void {
        this.filters = filters;
        this.updateSearchResults();
    }

    @action
    updateSearchResults(): void {
        const query = normalizeText(this.filters.search);

        if (!query) {
            this.searchResults = getInitialSuggestions(
                this.spatialLevels.spatialLevels,
            );
            return;
        }

        const searchResults = new Map<number, SearchResult>();

        // Recursive function to search through spatial levels and stations
        const processLevel = (level: Readonly<SpatialLevel>): void => {
            level.stations.forEach((station) => {
                if (normalizeText(station.displayName).includes(query)) {
                    searchResults.set(station.id.valueOrThrow, {
                        label: station.displayName,
                        id: station.id.valueOrThrow,
                        icon: station.icon,
                        isStation: true,
                        latitude: station.latitude.length
                            ? station.latitude
                            : null,
                        longitude: station.longitude,
                    });
                }
            });

            level.children?.forEach(processLevel);
        };

        // Iterate through all spatial levels and process them
        this.spatialLevels.spatialLevels.forEach(processLevel);

        // Convert results map to an array and update searchResults
        this.searchResults = Array.from(searchResults.values());
    }
}
